/**
 * The social product's reads and writes (lane ALGOBOO2; arbitrage docs/plans/ideas-2026-09-29.md section 4 and
 * section 2.4 of the product circles plan, `git show 878735fa32:docs/history/plans/2026-10/product-circles.md`): the front page ranked by today's numbers (FOMO1), shadowing an agent with
 * play money (SHADOW1), remixing one (REMIX1), the daily story (STORY1), the team an agent is (TEAM1) and friendly
 * challenges (CHALLENGE1). Every number is the platform's, read through the App API; algo.boo computes no result of
 * its own. An unknown is null beside the reason in plain words, never zero; money is an exact decimal of its integer
 * units (ETH of wei, a token of its base units); simulated results say so in `simulated`.
 */
import { z } from 'zod'
import { Address, DecimalString, Handle, IsoTimestamp, WeiString } from '../primitives.js'
import { AgentSummary } from './common.js'
import { ProductLimit } from './limits.js'

// ── the front page (FOMO1) ──────────────────────────────────────────────────────────────────────────────────

/** The book a result comes from: paper (the platform's fill model) or live (the chain). */
export const ResultBook = z.enum(['paper', 'live'])
export type ResultBook = z.output<typeof ResultBook>

/**
 * What an agent is doing, in a few words (B11): trading live, trading on paper, paused by its owner, or waiting for
 * its first trade. From its status and the book its result comes from; `words` never claims more.
 */
export const AgentStateWords = z.object({
  state: z.enum(['trading_live', 'trading_paper', 'paused', 'waiting', 'retired']),
  words: z.string(),
})
export type AgentStateWords = z.output<typeof AgentStateWords>

/**
 * The platform's state of the strategy an agent runs (A2-2): admitted (saved, not started), running, paused or
 * retired, as the App API's strategy read states it; null with `words` saying why when it could not be read. `hold`
 * says why it does not trade where it does not, and `next` what would change that. Whether its program is awake this
 * very second is not served (`liveness`, `RUNNER_LIVENESS_NOT_SERVED`).
 */
export const RunnerState = z.object({
  status: z.enum(['admitted', 'running', 'paused', 'retired']).nullable(),
  words: z.string(),
  hold: z.string().nullable(),
  next: z.string().nullable(),
  liveness: ProductLimit,
})
export type RunnerState = z.output<typeof RunnerState>

/** One agent on the front page's ranking of today's results. */
export const FrontAgentRow = z.object({
  /**
   * The agent's place under the platform's one ranking rule (the App API's standings, the same read as
   * `GET /standings/week`, lane BOOCLEAN1), in its book's list: live first, then paper, labelled simulated.
   */
  rank: z.number().int().positive(),
  agent: AgentSummary,
  book: ResultBook,
  /** The week's net result in ETH (realized less gas and fees), exact, as the platform states it. */
  netEth: DecimalString,
  roundTrips: z.number().int().nonnegative(),
  tradesCount: z.number().int().nonnegative(),
  state: AgentStateWords,
  runner: RunnerState,
  /** When the platform stated the result. */
  asOf: IsoTimestamp.nullable(),
})
export type FrontAgentRow = z.output<typeof FrontAgentRow>

/** One agent token on the front page: its price and market cap in its pool's quote token, and its day's move. */
export const FrontTokenRow = z.object({
  agent: AgentSummary,
  address: Address,
  symbol: z.string(),
  name: z.string(),
  quoteSymbol: z.string().nullable(),
  /** One token's price in quote units now (the pool read live), exact; null with `unavailable` when it cannot be read. */
  price: DecimalString.nullable(),
  /** Price times supply, in quote units, exact; null with the price. */
  marketCap: DecimalString.nullable(),
  /** The price's change over the last 24 hours in percent (two decimals); null when no price a day old is known. */
  change24hPct: DecimalString.nullable(),
  holdersCount: z.number().int().nonnegative().nullable(),
  launchedAt: IsoTimestamp.nullable(),
  /** Why the price, the cap or the change is missing, in plain words; null when all three are known. */
  unavailable: z.string().nullable(),
})
export type FrontTokenRow = z.output<typeof FrontTokenRow>

/**
 * What the agent saw and when, as the platform recorded its decision ("it saw it first"): the fact that triggered it
 * and the block it priced at, when it decided, and the block and time of the fill. How early it was against the first
 * public mention of the token is not shown (A2-1): `firstMention` says why.
 */
export const TradeReceipt = z.object({
  saw: z.string(),
  decidedAt: IsoTimestamp,
  decisionBlock: WeiString.nullable(),
  filledBlock: WeiString.nullable(),
  filledAt: IsoTimestamp.nullable(),
  firstMention: ProductLimit,
})
export type TradeReceipt = z.output<typeof TradeReceipt>

/** One trade of an agent as a card: what it did, on which book, and its receipt. */
export const FrontTradeCard = z.object({
  id: z.string(),
  agent: AgentSummary,
  side: z.enum(['buy', 'sell']),
  token: Address.nullable(),
  /** What the buy spent or the sell took, ETH exact; null when the trade was not against ETH or not filled. */
  amountEth: DecimalString.nullable(),
  book: ResultBook,
  outcome: z.enum(['filled', 'refused', 'open']),
  outcomeWords: z.string(),
  receipt: TradeReceipt,
})
export type FrontTradeCard = z.output<typeof FrontTradeCard>

/** `GET /front`: today's ranking, the agent tokens by price, the gainers, the new launches and the live trades. */
export const FrontPage = z.object({
  asOf: IsoTimestamp,
  agents: z.array(FrontAgentRow),
  /** Agents the standings list unranked (fewer than five closed round trips, or a figure not reported): counted, never ranked as zero. */
  unrankedCount: z.number().int().nonnegative(),
  tokens: z.array(FrontTokenRow),
  /** Tokens whose day's move is known, highest first; an unknown move is never a gainer. */
  gainers: z.array(FrontTokenRow),
  launches: z.array(FrontTokenRow),
  trades: z.array(FrontTradeCard),
  /** Whose trades the cards are (A2-3): algo.boo's agents' own, as the platform shows each app its own. */
  tradesScope: ProductLimit,
  /** Why a part is empty or missing, in plain words; null when it was read. */
  unavailable: z.object({ agents: z.string().nullable(), tokens: z.string().nullable(), trades: z.string().nullable() }),
})
export type FrontPage = z.output<typeof FrontPage>

// ── shadowing an agent with play money (SHADOW1) ───────────────────────────────────────────────────────────

/** An ETH amount as typed: a positive decimal with at most 18 fraction digits. */
export const EthAmount = z
  .string()
  .regex(/^(0|[1-9][0-9]{0,8})(\.[0-9]{1,18})?$/, 'an ETH amount, at most 18 decimals')
  .refine((s) => /[1-9]/.test(s), 'above zero')

/** `POST /agents/:handle/shadow`: follow the agent with play money at your size and delay. */
export const ShadowStart = z.object({
  /** The play money in all. */
  budgetEth: EthAmount,
  /** What each copied buy spends, at most the play money. */
  lotEth: EthAmount,
  /** How long after the agent each copy is decided: your own reaction time. */
  delaySeconds: z.number().int().min(0).max(3600),
})
export type ShadowStart = z.output<typeof ShadowStart>

/** How many copies ended one way, by the platform's reason, in plain words. */
export const ShadowReasonCount = z.object({ reason: z.string(), count: z.number().int().nonnegative(), words: z.string() })
export type ShadowReasonCount = z.output<typeof ShadowReasonCount>

/** One copy of one of the agent's trades, as the fill model ended it. */
export const ShadowCopyView = z.object({
  side: z.enum(['buy', 'sell']),
  token: Address,
  /** What it spent (wei for a buy, the token's base units for a sell) and got, exact integers; null when unknown. */
  amountIn: WeiString.nullable(),
  filledOut: WeiString.nullable(),
  gasEth: DecimalString.nullable(),
  outcome: z.string(),
  words: z.string(),
  decidedAt: IsoTimestamp.nullable(),
  /** The book the agent's own trade was on. */
  agentBook: ResultBook,
})
export type ShadowCopyView = z.output<typeof ShadowCopyView>

/**
 * What the shadow would have had, simulated by the platform's paper fill model when read: ETH as exact decimals of
 * the platform's wei (cash, realized and net signed). `heldAtCostEth` is the cash plus what the open lots cost -- the
 * open lots are counted, never marked to a price.
 */
export const ShadowResultView = z.object({
  simulated: z.literal(true),
  cashEth: DecimalString,
  heldAtCostEth: DecimalString,
  spentEth: DecimalString,
  proceedsEth: DecimalString,
  realizedEth: DecimalString,
  gasEth: DecimalString,
  /** Realized less every gas paid; null while a copy's gas is unknown. */
  netEth: DecimalString.nullable(),
  openLots: z.array(z.object({ token: Address, amount: WeiString, costEth: DecimalString })),
  copies: z.number().int().nonnegative(),
  filled: z.number().int().nonnegative(),
  reverted: z.number().int().nonnegative(),
  refused: z.array(ShadowReasonCount),
  skipped: z.array(ShadowReasonCount),
  notInEth: z.number().int().nonnegative(),
  /** False when the agent traded more than one read replays: the result covers its oldest trades only. */
  complete: z.boolean(),
  recent: z.array(ShadowCopyView),
})
export type ShadowResultView = z.output<typeof ShadowResultView>

/**
 * The fees a real follower of the agent pays, beside the simulated result (A2-5). The performance fee is the agent's
 * rate (`performanceFeeBps`, hundredths of a percent) of a permission's realized live profit above its high-water
 * mark, assessed monthly by the platform from the permission's lots; applied here to the shadow's realized result as
 * one period from its start (the first month's mark is zero). Gas is in the result already. `feeEth` and
 * `netAfterFeeEth` are null while the result is not read (a list) or its net is unknown. `assessedEth` is what was
 * assessed on the real permission once the shadow was upgraded (null before, or before a month closed).
 */
export const ShadowFees = z.object({
  performanceFeeBps: z.number().int().min(0).max(10_000),
  feeEth: DecimalString.nullable(),
  netAfterFeeEth: DecimalString.nullable(),
  assessedEth: DecimalString.nullable(),
  words: z.string(),
})
export type ShadowFees = z.output<typeof ShadowFees>

/** One shadow of an agent: its terms, its state, and on a read of it the simulated result. */
export const ShadowView = z.object({
  shadowRef: z.string().regex(/^shd_[0-9a-z]{16,40}$/),
  agent: AgentSummary.nullable(),
  strategyId: z.string(),
  state: z.enum(['active', 'stopped', 'upgraded']),
  budgetEth: DecimalString,
  lotEth: DecimalString,
  delaySeconds: z.number().nonnegative(),
  startedAt: IsoTimestamp,
  endedAt: IsoTimestamp.nullable(),
  permissionRef: z.string().nullable(),
  /**
   * Whether the agent can still be followed (lane BOOSOCIAL1): false once its strategy's owner made it private on the
   * platform, which ended the shadow; `upgrade.words` then says so.
   */
  leaderFollowable: z.boolean(),
  simulated: z.literal(true),
  /** Null in a list; a read of one computes it. */
  result: ShadowResultView.nullable(),
  /** What the simulation leaves out, in plain words. */
  notModelled: z.array(z.string()),
  modelWords: z.string().nullable(),
  /** The price move while a copy waits out the delay is not simulated (A2-4). */
  delayMove: ProductLimit,
  /** What a real follower pays (A2-5): the agent's fee policy, applied to this result. */
  fees: ShadowFees,
  /**
   * "Follow for real" (A2-6): offered on every active shadow at once -- no waiting period, no days of profit asked
   * first (`rule`) -- with what it does.
   */
  upgrade: z.object({ available: z.boolean(), rule: z.string(), words: z.string() }),
})
export type ShadowView = z.output<typeof ShadowView>

/** `POST /me/shadows/:ref/upgrade`: the permission the user just granted the agent. */
export const ShadowUpgrade = z.object({ permissionRef: z.string().regex(/^prm_[0-9a-z]{16,40}$/) })
export type ShadowUpgrade = z.output<typeof ShadowUpgrade>

// ── remixing an agent (REMIX1) ─────────────────────────────────────────────────────────────────────────────

/**
 * One behaviour a remix changes, in plain words, over the program's declared settings: less chasing (half the price
 * move and quote age it accepts), smaller positions (half of every size), only the wallets you name -- or any of its
 * declared settings by name, each within its type (`settings`, A2-7).
 */
export const RemixKnob = z.enum(['less_chasing', 'smaller_positions', 'only_wallets', 'settings'])
export type RemixKnob = z.output<typeof RemixKnob>

/** One setting a knob changes: its name in the program, its value now and after (JSON as the program reads it). */
export const RemixSettingChange = z.object({ name: z.string(), now: z.unknown(), after: z.unknown(), words: z.string() })
export type RemixSettingChange = z.output<typeof RemixSettingChange>

/** One setting the agent's program declares: its name, its type and unit as the program states them, and its value now. */
export const DeclaredSetting = z.object({ name: z.string(), type: z.string(), unit: z.string().nullable(), now: z.unknown(), words: z.string() })
export type DeclaredSetting = z.output<typeof DeclaredSetting>

/**
 * `GET /agents/:handle/remix`: what a remix of this agent may change, knob by knob, and why not where it cannot; every
 * setting its program declares (the `settings` knob); and that a new program line is the desk's (`programLine`).
 */
export const RemixOptions = z.object({
  agent: AgentSummary,
  strategyId: z.string(),
  settings: z.array(DeclaredSetting),
  programLine: ProductLimit,
  /** Set where the flow is unsupported (no declared settings for the one language): its owner and words; null where it is offered. */
  unsupported: z.object({ owner: z.string(), reason: z.string() }).nullable().optional(),
  knobs: z.array(
    z.object({
      knob: RemixKnob,
      words: z.string(),
      available: z.boolean(),
      reason: z.string().nullable(),
      changes: z.array(RemixSettingChange),
    }),
  ),
})
export type RemixOptions = z.output<typeof RemixOptions>

/** `POST /agents/:handle/remix`: the new agent's handle and name and the one behaviour it changes. */
export const RemixCreate = z.object({
  knob: RemixKnob,
  /** `only_wallets`: the wallets it copies, lower-case addresses. */
  wallets: z.array(Address).min(1).max(20).optional(),
  /** `settings`: the declared settings it changes, by name, each a value of its type (JSON as the program reads it). */
  settings: z.record(z.string().regex(/^[a-z_][a-z0-9_]{0,63}$/), z.unknown()).optional(),
  handle: z.string().regex(/^[a-z0-9_]{3,24}$/, 'handle must be 3-24 chars of a-z, 0-9, _'),
  name: z.string().min(1).max(60),
})
export type RemixCreate = z.input<typeof RemixCreate>

/** The draft agent a remix made: its strategy is a fork of the parent's with the change named. */
export const RemixResult = z.object({
  agent: AgentSummary,
  strategyId: z.string(),
  parent: AgentSummary,
  changes: z.array(RemixSettingChange),
  /** The agent is a draft: its owner activates it from its manage page. */
  words: z.string(),
})
export type RemixResult = z.output<typeof RemixResult>

/** One book's result over a window in ETH, exact; `netEth` null while a figure is unknown. */
export const BookResult = z.object({
  decisions: z.number().int().nonnegative(),
  filled: z.number().int().nonnegative(),
  refused: z.number().int().nonnegative(),
  openLots: z.number().int().nonnegative(),
  realizedEth: DecimalString,
  gasEth: DecimalString,
  feesEth: DecimalString,
  netEth: DecimalString.nullable(),
})
export type BookResult = z.output<typeof BookResult>

/** `GET /agents/:handle/lineage`: where it came from, what came of it, and both results since the fork. */
export const AgentLineage = z.object({
  agentId: z.string(),
  strategyId: z.string().nullable(),
  parent: z
    .object({
      agent: AgentSummary.nullable(),
      strategyId: z.string(),
      name: z.string().nullable(),
      readable: z.boolean(),
      forkedAt: IsoTimestamp.nullable(),
      /** The chain's newest block when it was forked, as the platform recorded it; null with `forkBlockUnknown`. */
      forkBlock: WeiString.nullable(),
      forkBlockUnknown: ProductLimit.nullable(),
    })
    .nullable(),
  sinceFork: z
    .object({
      since: IsoTimestamp.nullable(),
      self: z.object({ paper: BookResult, live: BookResult }),
      parent: z.object({ paper: BookResult, live: BookResult }).nullable(),
    })
    .nullable(),
  children: z.array(z.object({ agent: AgentSummary.nullable(), strategyId: z.string(), name: z.string(), forkedAt: IsoTimestamp.nullable() })),
  unavailable: z.string().nullable(),
})
export type AgentLineage = z.output<typeof AgentLineage>

// ── the daily story (STORY1; algo.boo's own since BOOSTORY1) ──────────────────────────────────────────────────

/** One record a story's line rests on, in words: a buy, a trade it passed on, a fill, a sale, a step, the day's result, a note. */
export const StoryEvidence = z.object({
  id: z.string(),
  kind: z.enum(['decision', 'refusal', 'fill', 'exit', 'lifecycle', 'result', 'note', 'other']),
  words: z.string(),
  at: IsoTimestamp.nullable(),
})
export type StoryEvidence = z.output<typeof StoryEvidence>

/** One line of a card: the sentence and each record it rests on. */
export const StoryLineView = z.object({ text: z.string(), evidence: z.array(StoryEvidence).min(1) })
export type StoryLineView = z.output<typeof StoryLineView>

/** What followed one of the moments a pool was still tradable after a refusal, at one horizon. */
export const AftermathHorizon = z.object({
  afterBlocks: z.number().int().positive(),
  /** Refusals whose pool state is known this many blocks later, and of them the ones it could still trade. */
  withState: z.number().int().nonnegative(),
  stillTradable: z.number().int().nonnegative(),
  /** Refusals priced this many blocks later, and the median move of the price since, in hundredths of a percent; null when none was priced (never zero). */
  priced: z.number().int().nonnegative(),
  medianMarkoutBps: z.number().nullable(),
})
export type AftermathHorizon = z.output<typeof AftermathHorizon>

/** What followed the trades it passed on for one reason (A2-9). */
export const RefusalAftermath = z.object({
  reason: z.string(),
  words: z.string(),
  subjects: z.number().int().nonnegative(),
  horizons: z.array(AftermathHorizon),
})
export type RefusalAftermath = z.output<typeof RefusalAftermath>

/**
 * What followed the trades the agent passed on over the last week (A2-9): the platform's `view:strategy-refusals/1`,
 * per reason, whether the pool could still trade 1, 10 and 100 blocks later and where its price went, as far as the
 * platform derived (`throughBlock`). A refusal whose aftermath is not known stays out of the counts, never zero.
 */
export const StoryAftermath = z.object({
  windowDays: z.number().int().positive(),
  throughBlock: z.string().nullable(),
  reasons: z.array(RefusalAftermath),
})
export type StoryAftermath = z.output<typeof StoryAftermath>

/** A promotion of one of the agent's models that day, as the platform's receipt states it (A2-10). */
export const StoryPromotion = z.object({
  requestId: z.string(),
  model: z.string(),
  state: z.enum(['open', 'promoted', 'kept', 'refused', 'declined']),
  /** The model's version it made, where it promoted one. */
  version: z.number().int().nullable(),
  requestedAt: IsoTimestamp.nullable(),
  decidedAt: IsoTimestamp.nullable(),
  words: z.string(),
})
export type StoryPromotion = z.output<typeof StoryPromotion>

/**
 * How the day's cards reach the signed-in viewer (A2-11): whether they follow the agent (the subscription), whether
 * the story reaches them in the app and by push (their `daily_story` notification setting; turning both off is the
 * opt-out), and whether this day's did -- the notification, once, and its push to each of their browsers with its
 * outcome. Null for a visitor.
 */
export const StoryDelivery = z.object({
  following: z.boolean(),
  inApp: z.boolean(),
  push: z.boolean(),
  delivered: z
    .object({
      at: IsoTimestamp,
      push: z.array(z.object({ status: z.enum(['sent', 'failed', 'gone']), at: IsoTimestamp })),
    })
    .nullable(),
  words: z.string(),
})
export type StoryDelivery = z.output<typeof StoryDelivery>

/**
 * `GET /agents/:handle/story?day=`: the agent's story of one UTC day -- three cards (noticed, did, learned), composed by
 * algo.boo (lane BOOSTORY1) from that day's records as the platform serves them (`GET /v2/strategies/{id}/days/{day}`)
 * through one call of the platform's model gateway, every line citing them. `state` says whether it is written, being
 * written, not written yet or refused, with `words` for a person; `missing` what the records cannot say; `callRef` the
 * gateway's call the cards came from and `model` its model, once written. Beside it, from the platform's records: what
 * followed the trades it passed on this week (`aftermath`), the promotions of its models that day (`promotions`, each
 * with its receipt), each null or empty with the reason in words; and how the story reaches the viewer (`delivery`).
 */
export const AgentStory = z.object({
  agent: AgentSummary,
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  state: z.enum(['written', 'being_written', 'not_written', 'refused', 'unavailable']),
  words: z.string(),
  cards: z.object({ noticed: z.array(StoryLineView), did: z.array(StoryLineView), learned: z.array(StoryLineView) }).nullable(),
  missing: z.array(z.string()),
  /** The model gateway's call the cards came from (`mcl_…`, its record at `GET /v2/models/calls/{callRef}`), once written. */
  callRef: z.string().nullable(),
  /** The model that wrote the cards (`vendor/model`), once written. */
  model: z.string().nullable(),
  aftermath: StoryAftermath.nullable(),
  aftermathUnavailable: z.string().nullable(),
  promotions: z.array(StoryPromotion),
  promotionsUnavailable: z.string().nullable(),
  delivery: StoryDelivery.nullable(),
})
export type AgentStory = z.output<typeof AgentStory>

// ── the team an agent is, and teaching it a rule (TEAM1) ──────────────────────────────────────────────────

/** One member of an agent's team: the trader (its own program) or a scout whose list the trader reads. */
export const TeamMember = z.object({
  role: z.enum(['trader', 'scout']),
  strategyId: z.string(),
  name: z.string().nullable(),
  /** A public agent of algo.boo that runs this program, where there is one. */
  agent: AgentSummary.nullable(),
  /** What it does for the team, in words. */
  words: z.string(),
  /** Its result over the last 7 days (the platform's), each book apart; null where it cannot be read. */
  result: z.object({ paper: BookResult, live: BookResult }).nullable(),
  readable: z.boolean(),
})
export type TeamMember = z.output<typeof TeamMember>

/** One of the agent's recent decisions and what each member contributed, as far as it is recorded. */
export const TeamDecision = z.object({
  id: z.string(),
  at: IsoTimestamp,
  token: Address.nullable(),
  book: ResultBook,
  outcomeWords: z.string(),
  parts: z.array(z.object({ strategyId: z.string(), role: z.enum(['trader', 'scout']), words: z.string(), recorded: z.boolean() })),
})
export type TeamDecision = z.output<typeof TeamDecision>

/** `GET /agents/:handle/team`: the team the agent already is -- its trader and its scouts -- and who did what. */
export const AgentTeam = z.object({
  agent: AgentSummary,
  members: z.array(TeamMember),
  decisions: z.array(TeamDecision),
  /** Which scout list a trade passed is not recorded (A2-12): said where a scout's part would be; null without scouts. */
  partsUnavailable: ProductLimit.nullable(),
  unavailable: z.string().nullable(),
})
export type AgentTeam = z.output<typeof AgentTeam>

/** `POST /agents/:handle/rules/propose`: a rule in the owner's words. */
export const RuleProposalRequest = z.object({ sentence: z.string().trim().min(3).max(300) })
export type RuleProposalRequest = z.input<typeof RuleProposalRequest>

/** One of last week's decisions under the proposed rule: what it did, and what it would have done where that is known. */
export const RuleExample = z.object({ decisionId: z.string(), at: IsoTimestamp, did: z.string(), wouldHave: z.string() })
export type RuleExample = z.output<typeof RuleExample>

/**
 * The rule a sentence becomes, shown exactly before it is made: settings of the agent's program with their value now
 * and after (`kind: setting`), or -- where no declared setting says it -- a new line of its program, which the desk's
 * chat writes and shows (`kind: program_line`), or not understood (`unclear`). With last week's decisions under it
 * where the records can say, and why where they cannot.
 */
export const RuleProposal = z.object({
  sentence: z.string(),
  kind: z.enum(['setting', 'program_line', 'unclear']),
  settings: z.array(RemixSettingChange),
  words: z.string(),
  examples: z.array(RuleExample),
  /** Why last week's decisions could not be read just now; null when they were. */
  examplesUnavailable: z.string().nullable(),
  /** A rule about prices is not replayed on last week's trades (A2-13); null for a rule the records can show. */
  replay: ProductLimit.nullable(),
  /** Where a program line is needed: the desk, with the sentence. */
  deskHref: z.string().nullable(),
  activatable: z.boolean(),
})
export type RuleProposal = z.output<typeof RuleProposal>

/** `POST /agents/:handle/rules/activate`: the settings a proposal showed, made the agent's next version. */
export const RuleActivate = z.object({ sentence: z.string().trim().min(3).max(300), settings: z.record(z.string(), z.unknown()) })
export type RuleActivate = z.input<typeof RuleActivate>

export const RuleActivated = z.object({ version: z.number().int().positive(), strategyVersion: z.number().int().positive(), words: z.string() })
export type RuleActivated = z.output<typeof RuleActivated>

// ── friendly challenges (CHALLENGE1) ───────────────────────────────────────────────────────────────────────

/** `POST /challenges`: friends put agents side by side over the next week with the same play money. */
export const ChallengeCreate = z.object({
  title: z.string().trim().min(1).max(80),
  /** The agents, by handle: two to eight, each once. */
  agents: z
    .array(Handle)
    .min(2)
    .max(8)
    .refine((a) => new Set(a).size === a.length, 'each agent once'),
  /** The play money every agent's return is measured against. */
  budgetEth: EthAmount,
})
export type ChallengeCreate = z.output<typeof ChallengeCreate>

/** `POST /challenges/:id/entries`: add one more agent before the week starts. */
export const ChallengeEntryAdd = z.object({ handle: Handle })

/**
 * One agent's week in a challenge, its book beside the others: the week's net result, its costs, its worst drop, how
 * many days it sat out, and its return on the challenge's play money. Every figure is null while it is not known yet
 * (the week has not started, no day was read, or the platform does not know a figure), with the reason in `unscored`.
 */
export const ChallengeScore = z.object({
  agent: AgentSummary,
  strategyId: z.string(),
  book: ResultBook,
  /**
   * The agent's place among the challenge's agents under the platform's one ranking rule (the App API's standings,
   * lane BOOCLEAN1), in its book's list; null while the standings list it unranked or not at all, `rankWords` says why.
   */
  rank: z.number().int().positive().nullable(),
  rankWords: z.string().nullable(),
  decisions: z.number().int().nonnegative().nullable(),
  filled: z.number().int().nonnegative().nullable(),
  refused: z.number().int().nonnegative().nullable(),
  netEth: DecimalString.nullable(),
  gasEth: DecimalString.nullable(),
  feesEth: DecimalString.nullable(),
  /** The largest fall of the running result from its high over the week, in ETH, never negative (`drawdownBasis` of the board). */
  drawdownEth: DecimalString.nullable(),
  /** Days of the week (so far) it made no new decision: sitting out is scored, not hidden. */
  daysSatOut: z.number().int().nonnegative().nullable(),
  daysRead: z.number().int().nonnegative(),
  /** The week's net result over the play money, in percent, two decimals. */
  returnPct: z.string().nullable(),
  /** That return applied to the challenge's play money, in ETH: every agent on the same budget. */
  onBudgetEth: DecimalString.nullable(),
  unscored: z.string().nullable(),
})
export type ChallengeScore = z.output<typeof ChallengeScore>

/**
 * The conditions every agent of a challenge is compared under (A2-16), published on the board: the same play money
 * (`budgetEth`), the same forward week, each agent's return measured on its own book and applied to that amount, gas
 * and fees counted, the worst drop's basis, and what is not done (`sharedAccount`, `intraday`).
 */
export const ChallengeConditions = z.object({
  budgetEth: DecimalString,
  startsAt: IsoTimestamp,
  endsAt: IsoTimestamp,
  sizing: z.string(),
  costs: z.string(),
  /** The worst drop is read from the realized result once a UTC day, open positions not marked to a price. */
  drawdownBasis: z.literal('daily_realized'),
  sharedAccount: ProductLimit,
  intraday: ProductLimit,
})
export type ChallengeConditions = z.output<typeof ChallengeConditions>

/** A challenge and its scoreboard. */
export const ChallengeView = z.object({
  id: z.string().uuid(),
  title: z.string(),
  budgetEth: DecimalString,
  startsAt: IsoTimestamp,
  endsAt: IsoTimestamp,
  phase: z.enum(['upcoming', 'running', 'finished']),
  createdBy: z.object({ handle: z.string().nullable(), displayName: z.string().nullable() }),
  /** Paper books are the platform's fill model, not the chain: said once, on the board. */
  simulated: z.boolean(),
  scores: z.array(ChallengeScore),
  /** How the board is scored, in plain words. */
  rules: z.string(),
  /** The ranking rule in the platform's words, and when its standings were stated (null: not read). */
  rankRule: z.string(),
  standingsAsOf: IsoTimestamp.nullable(),
  conditions: ChallengeConditions,
  asOf: IsoTimestamp.nullable(),
})
export type ChallengeView = z.output<typeof ChallengeView>

/** `GET /me/challenges`: the challenges you made or have an agent in. */
export const ChallengeList = z.object({
  challenges: z.array(
    z.object({
      id: z.string().uuid(),
      title: z.string(),
      startsAt: IsoTimestamp,
      endsAt: IsoTimestamp,
      phase: z.enum(['upcoming', 'running', 'finished']),
      agents: z.number().int().nonnegative(),
    }),
  ),
})
export type ChallengeList = z.output<typeof ChallengeList>
